import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { PersonaCreationModal } from '@/components/personas/PersonaCreationModal';
import { PersonaListItem } from '@/components/personas/PersonaListItem';
import { PersonaDetailPanel } from '@/components/personas/PersonaDetailPanel';
import { usePersonaData, type LoadDataResult } from '@/hooks/usePersonaData';
import { usePersonaActions } from '@/hooks/usePersonaActions';
import type { Persona, Project } from '@/utils/pocketbase';

import { useTranslation } from 'react-i18next';
export default function PersonasPage() {
  const { t } = useTranslation('personas');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // UI state
  const [currentPersona, setCurrentPersona] = useState<Persona | null>(null);
  const [expandedPersonaId, setExpandedPersonaId] = useState<string | null>(null);

  // Tooltip state
  const [tooltipVisible, setTooltipVisible] = useState<{
    id: string;
    type: 'edit' | 'delete' | 'select';
  } | null>(null);

  // Modal state
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [personaToEdit, setPersonaToEdit] = useState<Persona | null>(null);

  // Data hook
  const {
    projects,
    personas,
    isInitialLoading,
    error,
    setError,
    loadData,
  } = usePersonaData(
    useCallback((result: LoadDataResult) => {
      if (result.personasList.length > 0) {
        const firstPersona = result.personasList[0];
        if (firstPersona) {
          setCurrentPersona(firstPersona);
          setExpandedPersonaId(firstPersona.id);
        }
      }
    }, [])
  );

  // Auto-select persona from recordId query param
  useEffect(() => {
    if (personas.length > 0) {
      const targetId = searchParams.get('recordId');
      if (targetId) {
        const target = personas.find(p => p.id === targetId);
        if (target) {
          setCurrentPersona(target);
          setExpandedPersonaId(target.id);
        }
      }
    }
  }, [personas, searchParams]);

  // Actions hook
  const {
    isLoading,
    isFree,
    canEdit,
    canDelete,
    canCreateNew,
    handleDeletePersona: doDeletePersona,
    handleEditPersona: doEditPersona,
  } = usePersonaActions(personas.length);

  const handleDeletePersona = (personaId: string) => {
    doDeletePersona(personaId, {
      setError,
      isCurrentPersona: currentPersona?.id === personaId,
      onClearCurrent: () => {
        setCurrentPersona(null);
        setExpandedPersonaId(null);
      },
      onLoadData: loadData,
    });
  };

  const handleEditPersona = (persona: Persona) => {
    doEditPersona(persona, {
      setError,
      onEdit: (p) => {
        setPersonaToEdit(p);
        setShowEditModal(true);
      },
    });
  };

  const handleSelectPersona = (persona: Persona) => {
    setCurrentPersona(persona);
    setExpandedPersonaId(persona.id);
  };

  // Initial Loading State
  if (isInitialLoading) {
    return (
      <><PageHeader title={t('title')} description={t('description')} />
        <div className="flex items-center justify-center py-[80px]">
          <div className="w-[32px] h-[32px] border-2 border-[#2197f1] border-t-transparent rounded-full animate-spin" />
        </div>
      </>
    );
  }

  // Empty State - no personas
  if (personas.length === 0 && !isLoading) {
    return (
      <><PageHeader title={t('title')} description={t('description')} />
        <div className="flex flex-col gap-[24px]">
          <div className="font-segoe text-[16px] leading-[24px] text-[#565858] max-w-[769px]">
            <p>
              {t('empty.text')}
            </p>
          </div>

          <button
            onClick={() => setShowCreationModal(true)}
            className="relative inline-block"
          >
            <div className="bg-[#2197f1] hover:bg-[#1a7bc7] rounded-[16px] px-[24px] py-[12px] transition-colors inline-block">
              <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                {t('empty.createButton')}
              </p>
            </div>
          </button>
        </div>

        {/* Persona Creation Modal */}
        <PersonaCreationModal
          isOpen={showCreationModal}
          onClose={() => setShowCreationModal(false)}
          onSuccess={async (persona) => {
            setShowCreationModal(false);
            setCurrentPersona(persona);
            setExpandedPersonaId(persona.id);
            await loadData();
          }}
        />
      </>
    );
  }

  // Main State - personas list and detail view
  return (
    <>
      <div className="flex flex-col lg:flex-row gap-[24px]">
        {/* Left Sidebar - Personas List */}
        <div className="flex flex-col gap-[8px] w-full lg:w-[424px] lg:flex-shrink-0 lg:sticky lg:top-[24px] lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto">
          <PageHeader title={t('title')} description={t('description')} />
          {/* Personas List */}
          {personas.map((persona, index) => {
            const isDisabled = isFree && index > 0;
            const project = projects.find((p: Project) => p.id === persona.project);
            const projectName = project?.name || t('defaultProjectName');

            return (
              <PersonaListItem
                key={persona.id}
                persona={persona}
                projectName={projectName}
                isActive={expandedPersonaId === persona.id}
                isDisabled={isDisabled}
                tooltipVisible={tooltipVisible}
                onSelect={handleSelectPersona}
                onTooltipEnter={setTooltipVisible}
                onTooltipLeave={() => setTooltipVisible(null)}
              />
            );
          })}

          {/* Create New Button */}
          <button
            onClick={() => {
              if (canCreateNew) {
                setShowCreationModal(true);
              } else {
                setError(t('error.freeLimitReached'));
                navigate('/subscription');
              }
            }}
            disabled={!canCreateNew}
            onMouseEnter={() =>
              !canCreateNew && setTooltipVisible({ id: 'create-new', type: 'select' })
            }
            onMouseLeave={() => setTooltipVisible(null)}
            className="relative"
          >
            <div
              className={`${
                canCreateNew
                  ? 'bg-[var(--main-brand,#2197f1)] hover:opacity-90'
                  : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
              } rounded-[16px] px-[24px] py-[12px] transition-opacity`}
            >
              <p
                className={`font-segoe font-semibold text-[16px] leading-[24px] ${canCreateNew ? 'text-[color:var(--white,#ffffff)]' : 'text-[color:var(--main-brand,#2197f1)]'} text-center`}
              >
                {t('list.createButton')}
              </p>
            </div>

            {/* Tooltip for disabled create */}
            {tooltipVisible?.id === 'create-new' && !canCreateNew && (
              <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                  {t('tooltip.freeLimitCreate')}
                </p>
              </div>
            )}
          </button>

          {/* Limit Warning for Free Tier */}
          {isFree && personas.length >= 1 && (
            <div className="mt-[16px] border border-[#ff9196] rounded-[16px] p-[24px]">
              <div className="flex flex-col gap-[16px]">
                <div className="flex items-start gap-[8px]">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    className="flex-shrink-0 mt-[2px]"
                  >
                    <path
                      d="M10 7V11"
                      stroke="#0D0D0D"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 16.6667C13.6819 16.6667 16.6667 13.6819 16.6667 10C16.6667 6.3181 13.6819 3.33333 10 3.33333C6.3181 3.33333 3.33333 6.3181 3.33333 10C3.33333 13.6819 6.3181 16.6667 10 16.6667Z"
                      stroke="#0D0D0D"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.99609 13.3333H10.0036"
                      stroke="#0D0D0D"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div>
                    <p className="font-manrope font-semibold text-[20px] leading-[28px] text-[#0d0d0d] mb-[8px]">
                      {t('limitBanner.title')}
                    </p>
                    <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                      {t('limitBanner.description')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/subscription')}
                  className="bg-[#2197f1] hover:bg-[#1a7bc7] rounded-[16px] px-[24px] py-[12px] transition-colors"
                >
                  <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                    {t('limitBanner.button')}
                  </p>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Content - Selected Persona */}
        {currentPersona && expandedPersonaId === currentPersona.id && (
          <PersonaDetailPanel
            persona={currentPersona}
            projects={projects}
            canEdit={canEdit}
            canDelete={canDelete}
            tooltipVisible={tooltipVisible}
            onDelete={handleDeletePersona}
            onEdit={handleEditPersona}
            onTooltipEnter={setTooltipVisible}
            onTooltipLeave={() => setTooltipVisible(null)}
          />
        )}
      </div>

      {/* Error toast */}
      {error && (
        <div className="fixed bottom-[24px] right-[24px] bg-white border border-[#ff9196] rounded-[16px] p-[16px] z-50">
          <p className="font-segoe text-[14px] leading-[20px] text-[#ff5b63]">{error}</p>
        </div>
      )}

      {/* Persona Creation Modal */}
      <PersonaCreationModal
        isOpen={showCreationModal}
        onClose={() => setShowCreationModal(false)}
        onSuccess={async (persona) => {
          setShowCreationModal(false);
          setCurrentPersona(persona);
          setExpandedPersonaId(persona.id);
          await loadData();
        }}
      />

      {/* Persona Edit Modal */}
      <PersonaCreationModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setPersonaToEdit(null);
        }}
        onSuccess={async (persona) => {
          setShowEditModal(false);
          setPersonaToEdit(null);
          setCurrentPersona(persona);
          await loadData();
        }}
        persona={personaToEdit}
        isEditing={true}
      />
    </>
  );
}
